import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import type { ColdSearchResult } from "@/types/domain";

import { openWorkUaResult } from "@/api/queries";
import { safeAlert, safeConfirm, safeHaptic } from "@/lib/telegramSafe";
import {
  WORKUA_OPEN_PRICE,
  WORKUA_OPEN_TEXT,
  hasContacts,
  workUaOpenApplies,
  workUaOpenButtonReason,
  workUaOpenInFlight,
  workUaOpenSettledPaid,
} from "@/lib/workuaOpen";

interface WorkUaOpenControlsProps {
  result: ColdSearchResult;
  /** Whether the free re-score may be offered. Its row carries no block of
   * its own (nothing is left to buy), so a viewer who may not run this
   * vacancy's search learns it only from the 403 — unless the screen
   * already knows, e.g. the vacancy is unclaimed. */
  mayOpen: boolean;
  /** Runs after every click, whatever it answered: a 409 for the cap or the
   * switch means the row on screen is stale too. */
  onSettled: () => void;
}

/**
 * «Відкрити контакт (~25 грн)» for a Work.ua row: one paid GET /resume per
 * résumé, ever. The click only queues it; the row then reads
 * queued → calling → opened | already_known | ambiguous | failed, and the
 * screen re-reads it while it is in flight.
 *
 * No resend for an `ambiguous` open here: only an admin may send it again,
 * and the Mini App does not know the viewer's role, so that button lives in
 * the web admin.
 */
export default function WorkUaOpenControls({
  result,
  mayOpen,
  onSettled,
}: WorkUaOpenControlsProps): JSX.Element | null {
  // The free re-score queues one AI call per click and the server does not
  // dedupe them, so a second click on this mount is not offered.
  const [freeSent, setFreeSent] = useState(false);
  const open = useMutation({
    mutationFn: (_free: boolean) =>
      openWorkUaResult(result.cold_search_id, result.id),
    onSuccess: (_row, free) => {
      safeHaptic("success");
      if (free) setFreeSent(true);
    },
    onError: (err: Error) => {
      safeHaptic("error");
      safeAlert(`Не вдалося відкрити контакт: ${err.message}`);
    },
    onSettled,
  });

  if (!workUaOpenApplies(result)) return null;

  const status = result.workua_open_status ?? null;
  const block = result.workua_open_block ?? null;
  const reason = workUaOpenButtonReason(result);
  const showPaidButton = result.can_open_workua === true || reason !== null;
  const ambiguous = status === "ambiguous" || block === "ambiguous";
  const settledPaid = workUaOpenSettledPaid(result);
  const contacts = hasContacts(result);
  const showFreeButton =
    settledPaid && mayOpen && (!contacts || !result.match_score);
  const { phone_e164: phone, email } = result.candidate;

  const confirmPaidOpen = (): void => {
    safeConfirm(
      `Відкрити контакт «${result.candidate.full_name}» на Work.ua? Це списує один контакт із пакета (${WORKUA_OPEN_PRICE}). Одне резюме оплачується лише раз.`,
      (confirmed) => {
        if (confirmed) open.mutate(false);
      },
    );
  };

  return (
    <div className="flex flex-col gap-1 text-xs">
      {workUaOpenInFlight(result) && (
        <div className="text-tg-hint">
          {status === null
            ? "Відкривається інше резюме цієї людини…"
            : "Відкривається…"}
        </div>
      )}
      {settledPaid &&
        (contacts ? (
          <div className="flex flex-col gap-0.5">
            {phone && (
              <a
                href={`tel:${phone}`}
                className="select-all self-start text-sm text-tg-link underline"
              >
                {phone}
              </a>
            )}
            {email && (
              <a
                href={`mailto:${email}`}
                className="select-all self-start break-all text-sm text-tg-link underline"
              >
                {email}
              </a>
            )}
          </div>
        ) : (
          <div className="text-tg-hint">
            Контакт оплачено, але ще не підтягнуто
          </div>
        ))}
      {status === "failed" && (
        <div className="text-rose-500">{WORKUA_OPEN_TEXT.failed}</div>
      )}
      {ambiguous && (
        <div className="text-amber-700">{WORKUA_OPEN_TEXT.ambiguous}</div>
      )}
      {showPaidButton && (
        <button
          type="button"
          onClick={confirmPaidOpen}
          disabled={!result.can_open_workua || open.isPending}
          className="rounded bg-tg-button py-2 text-xs text-tg-buttonText disabled:opacity-50"
        >
          {open.isPending
            ? "Відкриваю…"
            : `Відкрити контакт (${WORKUA_OPEN_PRICE})`}
        </button>
      )}
      {reason && <div className="text-amber-700">{reason}</div>}
      {showFreeButton && (
        <button
          type="button"
          onClick={() => open.mutate(true)}
          disabled={open.isPending || freeSent}
          className="rounded border border-tg-secondary py-2 text-xs disabled:opacity-50"
        >
          {freeSent
            ? "Запит надіслано"
            : open.isPending
              ? "Надсилаю…"
              : contacts
                ? "Оцінити (безкоштовно)"
                : "Підтягнути контакт і оцінити"}
        </button>
      )}
    </div>
  );
}
